package com.arena_web.ui.sections.profile.edit

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.events.PopupOverlayFadeIn
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.ModalFadeInKeyframes
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

internal const val EDIT_MODAL_SURFACE = "#09142d"

// Plain inner fills for fields and pills — the gradient outline is painted by
// GradientBorderRing as a separate overlay so the element's own background stays
// untouched (matches the pattern used by the chat search input).
internal val EDIT_FIELD_BG =
    AppGradients.InteractiveSecondary
internal val EDIT_PILL_BG =
    AppGradients.InteractiveSecondary
internal const val EDIT_SECTION_DIVIDER = "0.5px solid rgba(148, 149, 152, 0.25)"

/**
 * Gradient-border overlay using the chat-search trick: an absolutely positioned
 * Box with `mask-composite` so only the outline picks up
 * `AppGradients.ButtonBorderGradientPrimary`. The parent MUST be `position: relative`
 * and have a matching `border-radius`; the overlay is `pointer-events: none` so it
 * never blocks input.
 */
/**
 * Horizontal section divider — fading linear gradient (transparent → 30%
 * grey → transparent), same look as the header divider in the Create Post
 * popup and the profile basic-info panel. Use in place of a solid
 * `border-bottom: EDIT_SECTION_DIVIDER` between modal sections.
 */
@Composable
internal fun SectionGradientDivider() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(1)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"
                )
            }
    )
}

@Composable
internal fun GradientBorderRing(radiusPx: Number, borderWidthPx: Number = 0.3) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "${borderWidthPx}px solid transparent")
                property("border-radius", s(radiusPx))
                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
            }
    )
}

@Composable
fun ProfileEditShell(
    isOpen: Boolean,
    title: String,
    description: String?,
    onClose: () -> Unit,
    onCancel: () -> Unit,
    onSave: () -> Unit,
    saveEnabled: Boolean = true,
    saveLabel: String? = null,
    headerTrailing: (@Composable () -> Unit)? = null,
    counterRow: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit
) {
    if (!isOpen) return

    val t = LocalAppStrings.current

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { onClose() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .sBorderRadius(26)
                .onClick { it.stopPropagation() }
                .overflow(Overflow.Hidden)
                .styleModifier {
                    // Width: scaled 700px (matches Figma `Focus & Activities` 7744:308745
                    // and other edit-shell screens), capped at 92vw on narrower viewports.
                    property("width", "min(${s(700)}, 92vw)")
                    property("max-height", "90vh")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("background-color", EDIT_MODAL_SURFACE)
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                }
                .animation(
                    ModalFadeInKeyframes.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(22)
                    .styleModifier {
                        property("flex", "1 1 auto")
                        property("min-height", "0")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255, 255, 255, 0.15) transparent")
                    }
            ) {
                // Header: title + description on left, Public info on right.
                // Bottom gradient divider matches the inter-section dividers
                // below (replaces the previous solid `border-bottom`).
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sPaddingBottom(12)
                        .styleModifier { property("align-items", "flex-end") },
                    verticalAlignment = Alignment.Bottom
                ) {
                    Column(
                        modifier = Modifier.flexGrow(1).sGap(8),
                    ) {
                        Row(verticalAlignment = Alignment.Bottom, modifier = Modifier.sGap(12)) {
                            SpanText(
                                title,
                                modifier = Modifier
                                    .sFontSize(24)
                                    .fontWeight(AppTypography.Medium)
                                    .sLineHeight(32)
                                    .sLetterSpacing(0.24)
                                    .color(AppColors.Text.Secondary)
                            )
                            if (headerTrailing != null) headerTrailing()
                        }
                        if (description != null) {
                            SpanText(
                                description,
                                modifier = Modifier
                                    .sFontSize(16)
                                    .fontWeight(AppTypography.Regular)
                                    .sLineHeight(24)
                                    .sLetterSpacing(0.16)
                                    .color(AppColors.Text.OnSurfaceMuted)
                            )
                        }
                    }
                    PublicInfoBadge()
                }
                SectionGradientDivider()

                // Optional counter / clear-all row (Focus & Activities)
                if (counterRow != null) {
                    Box(modifier = Modifier.fillMaxWidth().sPaddingTop(16)) {
                        counterRow()
                    }
                }

                // Body
                Box(modifier = Modifier.fillMaxWidth().sPaddingTop(if (counterRow != null) 16 else 32)) {
                    content()
                }

                // Action buttons
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sPaddingTop(24)
                        .sGap(12)
                        .styleModifier { property("justify-content", "flex-end") },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    GhostPillButton(t.commonCancel, onClick = onCancel)
                    PrimaryOrGhostPillButton(
                        label = saveLabel ?: t.commonSave,
                        primary = saveEnabled,
                        onClick = { if (saveEnabled) onSave() },
                    )
                }
            }
        }
    }
}

@Composable
private fun PublicInfoBadge() {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.sGap(8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(src = AppIcons.Globe, modifier = Modifier.sSize(16))
        SpanText(
            t.profileEditShellPublicInfo,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .sLetterSpacing(0.028)
                .color(AppColors.Text.OnSurfaceMuted)
        )
    }
}

@Composable
internal fun GhostPillButton(label: String, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .sHeight(46)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("position", "relative")
                property("background", EDIT_PILL_BG)
            }
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        GradientBorderRing(radiusPx = 100)
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("padding", "${s(14)} ${s(32)}")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                }
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Text.Secondary)
            )
        }
    }
}

@Composable
internal fun PrimaryOrGhostPillButton(label: String, primary: Boolean, onClick: () -> Unit) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .sHeight(46)
            .sBorderRadius(100)
            .cursor(if (primary) Cursor.Pointer else Cursor.NotAllowed)
            .styleModifier {
                property("position", "relative")
                property("background-color", "#0061FF")
                if (!primary) property("opacity", "0.45")
            }
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        GradientBorderRing(radiusPx = 100)
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("padding", "${s(14)} ${s(32)}")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                }
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(sportColors.onPrimary)
            )
        }
    }
}
